import request from '@/modules/base/util/request'
import config from '@/config'
import moment from 'moment'
import { csatFeaturesMapping, csatShowAgainMapping } from '@/modules/base/components/csat-modal/constants'
import { dispatch, getAppState } from '@/redux/store'
import { patchData } from '@/redux/ducks/user/info'
import { pick } from 'lodash'

const baseUrl = `${config.resourceHost}`

interface CreateFeatureData {
  feature: string;
  openAfterCount?: number;
  rating: number; // number of 1-5
  notes: string;
}
interface AbortFeatureData {
  feature: string;
  openAfterCount?: number;
  showAgain: boolean;
}
interface CsatEntry {
  feature: string,
  showAgainAt?: Date,
  openAfterCount?: number,
  openAfterCountFulfilled?: boolean,
  lastUsedAt?: Date,
  usedCount?: number,
}

export const CsatService = {
  createFeature: (data: CreateFeatureData): Promise<void> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tracking`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data,
      })
        .then(({ response: { data: { data } } }) => {
          dispatch(patchData({ csatTrackings: data }))
          resolve()
        })
        .catch(error => reject(error))
    })
  },
  abortFeature: (data: AbortFeatureData): Promise<void> => {
    return new Promise((resolve, reject) => {
      request(`${baseUrl}tracking/abort`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data,
      })
        .then(({ response: { data: { data } } }) => {
          dispatch(patchData({ csatTrackings: data }))
          resolve()
        })
        .catch(error => reject(error))
    })
  },
  updateStoredFeatures: ({ feature, openAfterCount, openAfterCountFulfilled }) => {
    const csatTrackings = getAppState().user.info.csatTrackings

    const existing = csatTrackings.find((item) => item.feature === csatFeaturesMapping[feature])
    const entry: CsatEntry = existing ?? { feature: csatFeaturesMapping[feature], showAgainAt: null, openAfterCount }

    const showAgainAtDefinition = csatShowAgainMapping.get(feature)
    if (showAgainAtDefinition) {
      entry.showAgainAt = moment().add(showAgainAtDefinition.amount, showAgainAtDefinition.unit).toDate()
    }

    if (openAfterCount) {
      if (existing) {
        const { lastUsedAt } = existing
        const diff = lastUsedAt && moment().diff(lastUsedAt, 'days')
        if (!lastUsedAt || diff > 0) {
          entry.usedCount = entry.usedCount ? entry.usedCount + 1 : 1
          entry.lastUsedAt = new Date()
        }
      } else {
        entry.usedCount = entry.usedCount ? entry.usedCount + 1 : 1
        entry.lastUsedAt = new Date()
      }

      entry.openAfterCountFulfilled = !!openAfterCountFulfilled
    }

    request(`${baseUrl}tracking/update`, {
      method: 'PATCH',
      auth: true,
      isConcurrent: true,
      data: pick(entry, ['feature', 'usedCount', 'openAfterCount', 'openAfterCountFulfilled', 'lastUsedAt']),
    })
      .then(({ response: { data: { data } } }) => {
        dispatch(patchData({ csatTrackings: data }))
      })
  },
}

export default CsatService
